<template>
  <v-app>
    <v-app-bar app color="#b71c1c" dark dense>
      <v-btn icon @click="$router.push('/')" class="mr-2">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <v-avatar size="32" class="mr-2">
        <img src="/img/icons/android-chrome-512x512.png" alt="Logo" />
      </v-avatar>
      <responsive-toolbar-title class="white--text">
        Noormarkun VPK
      </responsive-toolbar-title>
    </v-app-bar>

    <v-main class="grey lighten-5">
      <v-container>
        <v-row justify="center">
          <v-col cols="12" md="8" lg="6">
            <v-card v-if="loading">
              <v-card-text class="text-center">
                <v-progress-circular
                  indeterminate
                  color="primary"
                ></v-progress-circular>
                <div class="mt-2">Ladataan kauppalistaa...</div>
              </v-card-text>
            </v-card>

            <v-card v-else-if="error">
              <v-card-title class="red--text">Virhe</v-card-title>
              <v-divider></v-divider>
              <v-card-text>{{ error }}</v-card-text>
            </v-card>

            <v-card v-else>
              <v-card-title class="headline">
                {{ event.description || "Muonitus" }} - Kauppalista
              </v-card-title>
              <v-card-subtitle>
                <div>{{ formattedDate }}</div>
                <div class="d-flex justify-space-between align-center mt-1">
                  <div class="font-weight-bold">
                    Arvioitu hlö-määrä: {{ event.headCountEstimate || 0 }}
                  </div>
                  <v-switch
                    v-model="showPerPersonUsage"
                    label="Näytä käyttö/hlö"
                    dense
                    hide-details
                    class="mt-0 pt-0"
                  ></v-switch>
                </div>
                <div v-if="event.diets" class="mt-2 red--text font-weight-bold">
                  <v-icon small color="red">mdi-alert</v-icon>
                  Huomioitavat erikoisruokavaliot:
                  <div class="text-body-2 black--text">{{ event.diets }}</div>
                </div>

                <div class="d-flex flex-wrap justify-space-around mt-4">
                  <div
                    v-if="event.kitchenLeader"
                    class="subtitle-1 font-weight-bold primary--text text-center mx-2"
                  >
                    <v-icon color="primary" class="mr-1">mdi-chef-hat</v-icon>
                    Keittiö: {{ event.kitchenLeader }}
                  </div>
                  <div
                    v-if="event.fieldLeader"
                    class="subtitle-1 font-weight-bold success--text text-center mx-2"
                  >
                    <v-icon color="success" class="mr-1"
                      >mdi-clipboard-account</v-icon
                    >
                    Kenttä: {{ event.fieldLeader }}
                  </div>
                </div>
              </v-card-subtitle>
              <v-tabs v-model="tab" grow background-color="transparent">
                <v-tab><v-icon left>mdi-cart</v-icon> Ostokset</v-tab>
                <v-tab><v-icon left>mdi-package-variant</v-icon> Pakkaus</v-tab>
              </v-tabs>
              <v-divider></v-divider>

              <v-tabs-items v-model="tab" touchless>
                <v-tab-item>
                  <v-card-text>
                    <v-textarea
                      v-model="event.publicNotes"
                      label="Muistiinpanot / Kommentit (näkyy asemalla)"
                      rows="2"
                      auto-grow
                      outlined
                      dense
                      class="mb-4"
                      @change="saveList"
                      hide-details="auto"
                    ></v-textarea>

                    <div
                      v-if="event.shoppingList && event.shoppingList.length > 0"
                      class="d-flex align-center mb-4"
                    >
                      <div class="flex-grow-1 mr-4">
                        <v-progress-linear
                          :value="shoppingPercentage"
                          height="20"
                          rounded
                          color="success"
                        >
                          <template v-slot:default="{ value }">
                            <strong class="white--text" style="font-size: 12px"
                              >{{ Math.ceil(value) }}% Kerätty</strong
                            >
                          </template>
                        </v-progress-linear>
                      </div>
                      <div class="text-caption grey--text text-no-wrap">
                        {{ shoppingCount }} / {{ event.shoppingList.length }}
                      </div>
                    </div>

                    <div
                      v-for="(group, category) in groupedShoppingList"
                      :key="category"
                    >
                      <div class="text-subtitle-2 grey--text mt-4 mb-1">
                        {{ category }}
                      </div>
                      <v-list dense class="elevation-1 rounded">
                        <template v-for="(item, i) in group">
                          <v-list-item
                            :key="i"
                            :class="{ 'grey lighten-4': item.collected }"
                          >
                            <v-list-item-action>
                              <v-checkbox
                                v-model="item.collected"
                                @change="saveList"
                                color="success"
                              ></v-checkbox>
                            </v-list-item-action>
                            <v-list-item-avatar
                              v-if="item.imageUrl"
                              tile
                              size="50"
                              class="mr-3 cursor-pointer"
                              @click="openImage(item.imageUrl)"
                            >
                              <v-img :src="item.imageUrl" contain></v-img>
                            </v-list-item-avatar>
                            <v-list-item-content>
                              <v-list-item-title
                                class="text-h6 text-wrap py-1"
                                :class="{
                                  'text-decoration-line-through grey--text text--darken-2':
                                    item.collected,
                                }"
                                style="line-height: 1.2"
                              >
                                {{ item.name }}
                              </v-list-item-title>
                              <v-list-item-subtitle class="text-subtitle-1">
                                {{ item.amount }} {{ item.unit }}
                                <span
                                  v-if="
                                    showPerPersonUsage &&
                                    event.headCountEstimate > 0
                                  "
                                  class="text-caption grey--text ml-2"
                                >
                                  ({{
                                    (
                                      item.amount / event.headCountEstimate
                                    ).toFixed(2)
                                  }}
                                  / hlö)
                                </span>
                              </v-list-item-subtitle>
                              <div
                                v-if="item.comment"
                                class="text-body-2 grey--text text--darken-2 mt-1"
                              >
                                {{ item.comment }}
                              </div>
                            </v-list-item-content>
                          </v-list-item>
                          <v-divider
                            :key="'div-' + i"
                            v-if="i < group.length - 1"
                          ></v-divider>
                        </template>
                      </v-list>
                    </div>
                  </v-card-text>
                </v-tab-item>
                <v-tab-item>
                  <v-card-text>
                    <div
                      v-if="neededChecklistItems.length === 0"
                      class="text-center grey--text pa-4"
                    >
                      Ei valittuja pakattavia tuotteita.
                    </div>

                    <div
                      v-if="neededChecklistItems.length > 0"
                      class="d-flex align-center mb-4"
                    >
                      <div class="flex-grow-1 mr-4">
                        <v-progress-linear
                          :value="packingPercentage"
                          height="20"
                          rounded
                          color="success"
                        >
                          <template v-slot:default="{ value }">
                            <strong class="white--text" style="font-size: 12px"
                              >{{ Math.ceil(value) }}% Pakattu</strong
                            >
                          </template>
                        </v-progress-linear>
                      </div>
                      <div class="text-caption grey--text text-no-wrap">
                        {{ packedCount }} / {{ neededChecklistItems.length }}
                      </div>
                    </div>

                    <v-list
                      dense
                      class="pa-0"
                      style="background-color: transparent"
                    >
                      <template v-for="(item, i) in neededChecklistItems">
                        <v-list-item
                          :key="i"
                          class="mb-2 rounded"
                          :class="{
                            'pl-8': item.indent,
                            'grey lighten-4':
                              event.packedItems &&
                              event.packedItems[item.label],
                            'white elevation-1': !(
                              event.packedItems && event.packedItems[item.label]
                            ),
                          }"
                        >
                          <v-list-item-action>
                            <v-checkbox
                              v-if="event.packedItems"
                              v-model="event.packedItems[item.label]"
                              @change="saveList"
                              color="success"
                              hide-details
                            ></v-checkbox>
                          </v-list-item-action>
                          <v-list-item-content>
                            <v-list-item-title
                              class="text-body-1"
                              :class="{
                                'text-decoration-line-through grey--text text--darken-1':
                                  event.packedItems &&
                                  event.packedItems[item.label],
                                'font-weight-medium': !item.indent,
                              }"
                            >
                              {{ item.label }}
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </template>
                    </v-list>
                  </v-card-text>
                </v-tab-item>
              </v-tabs-items>
              <v-card-actions>
                <v-spacer></v-spacer>
                <div class="caption grey--text">
                  Muutokset tallentuvat automaattisesti
                </div>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-main>

    <picture-dialog v-model="selectedPictureUrl"></picture-dialog>
  </v-app>
</template>

<script>
import { firestore as db } from "@/plugins/firebase.app";
import { doc, onSnapshot, updateDoc } from "firebase/firestore";
import { groupBy, debounce } from "lodash";
import dayjs from "dayjs";
import PictureDialog from "@/views/PictureDialog.vue";
import ResponsiveToolbarTitle from "@/components/ResponsiveToolbarTitle.vue";

export default {
  name: "PublicShoppingList",
  components: { PictureDialog, ResponsiveToolbarTitle },
  data() {
    return {
      event: null,
      loading: true,
      error: null,
      unsubscribe: null,
      showPerPersonUsage: false,
      selectedPictureUrl: null,
      categories: [],
      checklistItems: [],
      tab: 0,
    };
  },
  computed: {
    neededChecklistItems() {
      if (!this.event || !this.event.checklist) return [];

      // Use configured items or defaults if not found
      const allItems =
        this.checklistItems && this.checklistItems.length > 0
          ? this.checklistItems
          : [
              { label: "Muonituslaatikot" },
              { label: "mukit", indent: true },
              { label: "jätesäkit", indent: true },
              { label: "käsidesi", indent: true },
              { label: "tarvikkeet", indent: true },
              { label: "Kanisterit" },
              { label: "mehu", indent: true },
              { label: "vesi", indent: true },
              { label: "käsienpesu", indent: true },
              { label: "Termarit" },
              { label: "kahvi", indent: true },
              { label: "teevesi", indent: true },
              { label: "Kylmä/lämpölaatikot (1-2kpl)" },
              { label: "Taittopöydät (1-2kpl)" },
              { label: "Telttakatos / Ilmakaariteltat (harkinnan mukaan)" },
              { label: "Valaistus (pimeään aikaan)" },
              { label: "Muonitukseen tarvittava vaatetus" },
              { label: "Käsipaperit" },
            ];

      // Filter to show only items that are selected (true) in checklist
      return allItems.filter((item) => this.event.checklist[item.label]);
    },
    groupedShoppingList() {
      if (!this.event || !this.event.shoppingList) return {};
      const grouped = groupBy(
        this.event.shoppingList,
        (item) => item.category || "Muu"
      );
      // Sort keys based on categories prop order
      const sortedKeys = Object.keys(grouped).sort((a, b) => {
        const indexA = this.categories.indexOf(a);
        const indexB = this.categories.indexOf(b);
        // If both are in the list, sort by index
        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
        // If only A is in the list, it comes first
        if (indexA !== -1) return -1;
        // If only B is in the list, it comes first
        if (indexB !== -1) return 1;
        // If neither, sort alphabetically, but put "Muu" last
        if (a === "Muu") return 1;
        if (b === "Muu") return -1;
        return a.localeCompare(b);
      });

      const sortedGrouped = {};
      sortedKeys.forEach((key) => {
        sortedGrouped[key] = grouped[key];
      });
      return sortedGrouped;
    },
    shoppingCount() {
      if (!this.event || !this.event.shoppingList) return 0;
      return this.event.shoppingList.filter((item) => item.collected).length;
    },
    shoppingPercentage() {
      if (
        !this.event ||
        !this.event.shoppingList ||
        this.event.shoppingList.length === 0
      )
        return 0;
      return (this.shoppingCount / this.event.shoppingList.length) * 100;
    },
    packedCount() {
      if (!this.event || !this.event.packedItems) return 0;
      return this.neededChecklistItems.filter(
        (item) => this.event.packedItems[item.label]
      ).length;
    },
    packingPercentage() {
      if (this.neededChecklistItems.length === 0) return 0;
      return (this.packedCount / this.neededChecklistItems.length) * 100;
    },
    formattedDate() {
      if (!this.event || !this.event.date) return "";
      return dayjs(this.event.date.toDate()).format("DD.MM.YYYY HH:mm");
    },
  },
  async created() {
    // Fetch settings for categories
    try {
      const settingsRef = doc(db, "catering_settings", "general");
      // We use onSnapshot here too in case settings change, though getDoc would suffice
      onSnapshot(settingsRef, (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          this.categories = data.categories || [];
          this.checklistItems = data.checklistItems || [];
        } else {
          this.categories = [
            "HeVi",
            "Leipä",
            "Kuivatuotteet",
            "Maitotuotteet",
            "Juomat",
            "Tarvikkeet",
            "Muu",
          ];
        }
      });
    } catch (e) {
      console.log("Could not fetch categories settings", e);
      this.categories = [
        "HeVi",
        "Leipä",
        "Kuivatuotteet",
        "Maitotuotteet",
        "Juomat",
        "Tarvikkeet",
        "Muu",
      ];
    }

    const eventId = this.$route.params.id;
    if (!eventId) {
      this.error = "Virheellinen linkki";
      this.loading = false;
      return;
    }

    this.unsubscribe = onSnapshot(
      doc(db, "caterings", eventId),
      (doc) => {
        this.loading = false;
        if (doc.exists()) {
          const data = doc.data();
          if (data.deletedAt) {
            this.error = "Kauppalista on poistettu.";
            this.event = null;
          } else if (!data.publicAccess) {
            this.error = "Tämä kauppalista ei ole julkinen.";
            this.event = null;
          } else {
            this.event = { id: doc.id, ...data };
            if (!this.event.shoppingList) this.event.shoppingList = [];
            if (!this.event.checklist) this.event.checklist = {};
            if (!this.event.packedItems) this.event.packedItems = {};
          }
        } else {
          this.error = "Kauppalistaa ei löytynyt.";
        }
      },
      (err) => {
        console.error(err);
        this.loading = false;
        this.error = "Ei käyttöoikeutta tai virhe yhteydessä.";
      }
    );
  },
  beforeDestroy() {
    if (this.unsubscribe) this.unsubscribe();
  },
  methods: {
    saveList: debounce(async function () {
      if (!this.event) return;
      try {
        const ref = doc(db, "caterings", this.event.id);
        // Only update shoppingList to respect security rules
        await updateDoc(ref, {
          shoppingList: this.event.shoppingList,
          publicNotes: this.event.publicNotes || "",
          packedItems: this.event.packedItems || {},
        });
      } catch (e) {
        console.error("Error saving list:", e);
      }
    }, 500),
    openImage(url) {
      this.selectedPictureUrl = url;
    },
  },
};
</script>
